import React, { useMemo } from 'react'
import DownloadIconNoBg from '../../assets/icons/DownloadIconNoBg';
import { useTheme } from '../../middleware/ThemeContext';
import ViewIcon from '../../assets/icons/ViewIcon';
import FileIconNoName from '../../assets/icons/FileIconNoName';
import StyledText from '../StyledComponents/StyledText';
import Profilepic from "../../assets/images/profilePic.jpg";

interface FileUploadProps {
    signedUrl: string;
    fileSize?: string;
    uploadedAt?: string;
}

export type FileType =
  | "image"
  | "pdf"
  | "word"
  | "excel"
  | "powerpoint"
  | "text"
  | "zip"
  | "video"
  | "audio"
  | "unknown";

  export interface FileInfo {
    fullName: string;
    cleanName: string;
    extension: string;
    type: FileType;
    isImage: boolean;
    isPdf: boolean;
    isWord: boolean;
    isExcel: boolean;
    isPreviewable: boolean;
  }

const fileTypeMap: Record<string, FileType> = {
  jpg: "image",
  jpeg: "image",
  png: "image",
  gif: "image",
  svg: "image",
  webp: "image",
  bmp: "image",
  ico: "image",
  tif: "image",
  tiff: "image",
  avif: "image",

  pdf: "pdf",

  doc: "word",
  docx: "word",
  odt: "word",

  xls: "excel",
  xlsx: "excel",
  csv: "excel",

  ppt: "powerpoint",
  pptx: "powerpoint",

  txt: "text",

  zip: "zip",
  rar: "zip",
  "7z": "zip",

  mp4: "video",
  mkv: "video",

  mp3: "audio",
  wav: "audio",
};

// export function getFileType(fileName: string): FileType {
//   if (!fileName) return "unknown";
//   const ext = fileName.split(".").pop()?.toLowerCase() || "";
//   return fileTypeMap[ext] || "unknown";
// }

export function getFileName(input: string): string {
  if (!input) return "";

  try {
    const pathname = input.includes("http")
      ? new URL(input).pathname
      : input;

    return pathname.split("/").pop() || "";
  } catch {
    return "";
  }
}

// Remove UUID prefix (optional)
export function getCleanFileName(fileName: string): string {
  if (!fileName) return "";

  const parts = fileName.split("-");

  // UUID usually has 5 parts
  if (parts.length > 5) {
    return parts.slice(5).join("-");
  }

  return fileName;
}

// Get extension
export function getExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() || "";
}

// Get type
export function getFileType(extension: string): FileType {
  return fileTypeMap[extension] || "unknown";
}

// Main helper
export function getFileInfo(input: string): FileInfo {
  const fullName = getFileName(input);

  const cleanName = getCleanFileName(fullName);

  const extension = getExtension(fullName);

  const type = getFileType(extension);

  return {
    fullName,
    cleanName,
    extension,
    type,

    isImage: type === "image",
    isPdf: type === "pdf",
    isWord: type === "word",
    isExcel: type === "excel",

    isPreviewable:
      type === "image" ||
      type === "pdf" ||
      type === "text" ||
      type === "video",
  };
}

const FileIcon: React.FC<{ type: FileType }> = ({ type }) => {
  const styles: Record<FileType, { color: string; label: string }> = {
    pdf: { color: "#E53935", label: "PDF" },
    word: { color: "#2B579A", label: "DOC" },
    excel: { color: "#217346", label: "XLS" },
    powerpoint: { color: "#D24726", label: "PPT" },
    image: { color: "#1976D2", label: "IMG" },
    text: { color: "#616161", label: "TXT" },
    zip: { color: "#F9A825", label: "ZIP" },
    video: { color: "#7B1FA2", label: "VID" },
    audio: { color: "#00897B", label: "AUD" },
    unknown: { color: "#9E9E9E", label: "FILE" },
  };

  const style = styles[type];

  return (
    <div style={iconContainer}>
      {/* <div style={fileShape} /> */}
      <FileIconNoName />
      <div style={{ ...badgeStyle, background: style.color }}>
        {style.label}
      </div>
    </div>
  );
};

export function getFileNameFromPath(path: string): string {
  if (!path) return "";

  return path.split("/").pop() || "";
}

const FileUploadPreview:React.FC<FileUploadProps> = ({ signedUrl, fileSize, uploadedAt }) => {

    const {theme} = useTheme();

    const fileInfo = getFileInfo(signedUrl);

    const fileType = useMemo(() => fileInfo.type, [signedUrl]);

    const downloadFile = async (signedUrl: string, fileName: string) => {
        try {
            const response = await fetch(signedUrl);

            if (!response.ok) {
            throw new Error("Failed to download file");
            }

            const blob = await response.blob();

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Download error:", error);
        }
    };

    const previewFile = () => {
        window.open(signedUrl, "_blank");
    };

  return (
    <div style={container}>
      {signedUrl === null ? 
        <StyledText>No file found</StyledText>
      : (
      <>
      <div style={leftSection}>
        {fileType === 'image' ? 
          <img
            className="rounded"
            src={signedUrl || Profilepic}
            style={{ width: "40px", height: "40px" }}
            alt="Profile"
          />
        :
        <FileIcon type={fileInfo.type} />
        }

        <div>
          {/* <div style={fileNameStyle}>{getFileNameFromPath(fileName)}</div> */}
          <StyledText
            textColor={theme.CorporateTheme.colors.primary}
            fontSize='14px'
            fontWeight={600}
          >
            {getFileNameFromPath(fileInfo.cleanName)}
          </StyledText>

          {(fileSize || uploadedAt) && (
            <div style={metaStyle}>
              {fileSize}
              {fileSize && uploadedAt && " • "}
              {uploadedAt}
            </div>
          )}
        </div>
      </div>
      <div style={rightSection}>
        {/* <button style={iconButton} onClick={previewFile}>
          <ViewIcon color={theme.CorporateTheme.colors.textSecondary} />
        </button> */}

        <button style={iconButton} onClick={() => downloadFile(signedUrl, fileInfo.cleanName)}>
          <DownloadIconNoBg color={theme.CorporateTheme.colors.textPrimary} />
        </button>
      </div>
      </>
       )}
    </div>
  )
}

export default FileUploadPreview

const container: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "4px 0px",
//   border: "1px solid #E0E0E0",
  borderRadius: "8px",
//   background: "#F9F9F9",
};

const leftSection: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
};

const rightSection: React.CSSProperties = {
  display: "flex",
  gap: "8px",
};

const fileNameStyle: React.CSSProperties = {
  fontWeight: 600,
  color: "#1976D2",
};

const metaStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#757575",
};

const iconButton: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: "16px",
};

const iconContainer: React.CSSProperties = {
  position: "relative",
  width: "40px",
  height: "40px",
};

const fileShape: React.CSSProperties = {
  width: "100%",
  height: "100%",
  border: "1px solid #D0D0D0",
  borderRadius: "4px",
  background: "#FFF",
};

const badgeStyle: React.CSSProperties = {
  position: "absolute",
  bottom: "4px",
  left: "-6px",
  color: "#FFF",
  fontSize: "10px",
  fontWeight: 600,
  padding: "2px 6px",
  borderRadius: "4px",
};